import { IconNames } from "@kamae-apps/shared/Components/Fondations/Icons/IconNames.type"
import useBreakpoints from "@kamae-apps/shared/Hooks/useMediaQuery"
import { CompanyStatus } from "@kamae-apps/shared/Types/Company/CompanyStatus"
import { useMemo } from "react"
import { useUser } from "../../../../../../apps/reactfront/src/Contexts/User/UserContext"
import { useFeatureFlags } from "../../../../../../apps/reactfront/src/hooks/posthog/useFeatureFlags"

export type MenuEntry = {
  path: string
  iconName: IconNames
  displayNameKey: string
  isAdminRequired: boolean
  isTeamLeaderRequired: boolean
  isDisabled?: boolean
  tooltip?: string
}

export const useMenuEntry = (isAdmin: boolean, isTeamLeader: boolean) => {
  const { isMobile } = useBreakpoints()
  const { user } = useUser()
  const featureFlags = useFeatureFlags()

  const displayNewChallengePages = useMemo(() => featureFlags.includes("new-challenge"), [featureFlags])
  const displayDashboard = useMemo(
    () => featureFlags.includes("new-reporting") && user.company.status !== CompanyStatus.Starter,
    [featureFlags, user.company.status]
  )
  const displayDojoPage = useMemo(() => featureFlags.includes("seasonal-ranking"), [featureFlags])

  const allMenuEntries = useMemo<MenuEntry[]>(
    () =>
      [
        {
          path: "/home",
          iconName: displayNewChallengePages ? "home" : "tatami",
          displayNameKey: "app:sidebar.home.label",
          isAdminRequired: false,
          isTeamLeaderRequired: false,
        },
        !displayNewChallengePages && {
          path: "/elearning",
          iconName: "kamaeBeltOutlined",
          displayNameKey: "app:sidebar.elearning.label",
          isAdminRequired: false,
          isTeamLeaderRequired: false,
        },
        displayNewChallengePages && {
          path: "/tatami#trainings",
          iconName: "tatami",
          displayNameKey: "app:sidebar.tatami.label",
          isAdminRequired: false,
          isTeamLeaderRequired: false,
        },
        displayDojoPage && {
          path: "/dojo#users",
          iconName: "kamaeBeltOutlined",
          displayNameKey: "app:sidebar.dojo.label",
          isAdminRequired: false,
          isTeamLeaderRequired: false,
        },
        {
          path: "/phishing",
          iconName: "phishing",
          displayNameKey: "app:sidebar.phishing.label",
          isAdminRequired: !user.company.phishing_enabled,
          isTeamLeaderRequired: false,
          isDisabled: !user.company.phishing_enabled,
          tooltip: "app:sidebar.phishing.disabled",
        },
        {
          path: "/reflexes",
          iconName: "book",
          displayNameKey: "app:sidebar.reflexe.label",
          isAdminRequired: false,
          isTeamLeaderRequired: false,
        },
        displayDashboard
          ? {
              path: "/dashboard",
              iconName: "stat",
              displayNameKey: "app:sidebar.dashboard.label",
              isAdminRequired: true,
              isTeamLeaderRequired: featureFlags.includes("reporting-team-leader"),
            }
          : {
              path: "/supervision",
              iconName: "supervision",
              displayNameKey: "app:sidebar.supervision.label",
              isAdminRequired: true,
              isTeamLeaderRequired: false,
            },
        {
          path: "/organization",
          iconName: "dashboard",
          displayNameKey: "app:sidebar.organization.label",
          isAdminRequired: true,
          isTeamLeaderRequired: false,
        },
      ].filter(Boolean) as MenuEntry[],
    [user, displayDashboard, displayNewChallengePages, displayDojoPage, featureFlags]
  )

  const menuEntries = useMemo(() => {
    return isMobile
      ? allMenuEntries.filter(
          entry => !["/organization", "/supervision", ...(displayDojoPage ? ["/dashboard"] : [])].includes(entry.path)
        )
      : allMenuEntries
  }, [isMobile, allMenuEntries, displayDojoPage])

  const filteredMenuEntries = useMemo(() => {
    return menuEntries.filter(
      menuEntry =>
        (menuEntry.isAdminRequired && isAdmin) ||
        (menuEntry.isTeamLeaderRequired && isTeamLeader) ||
        (!menuEntry.isAdminRequired && !menuEntry.isTeamLeaderRequired)
    )
  }, [menuEntries, isAdmin, isTeamLeader])

  return { filteredMenuEntries }
}
